import { Dispatch } from 'react';

import { ApiError } from '@/features/api';
import TwoFactorAuthenticationService, {
  TwoFactorAuthentication,
  TwoFactorAuthenticationStatus,
  TwoFactorAuthenticationType,
} from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';
import * as companyService from '@/legacy/services/company';
import { Instance as UserService } from '@/legacy/services/user';
import { Action } from '@/legacy/utils/reactHelpers';

import Constants from '../shared/constants';

import { SmsCountry, VerificationStep } from './helper';

enum ActionType {
  AddErrors = 'AddErrors',

  SetSteps = 'SetSteps',
  NextStep = 'NextStep',
  PreviousStep = 'PreviousStep',

  SetFormPhoneNumber = 'SetFormPhoneNumber',
  SetFormCountry = 'SetFormCountry',
  SetFormVerificationMethod = 'SetFormVerificationMethod',
  SetDisableVerificationMethod = 'SetDisableVerificationMethod',
  SetCode = 'SetCode',

  HandleFormFieldErrors = 'HandleFormFieldErrors',
  ShowDialog = 'ShowDialog',
  CloseDialog = 'CloseDialog',
  ShowFieldError = 'ShowFieldError',
  UnableToScan = 'UnableToScan',
  ResendSmsCode = 'ResendSmsCode',
  CallUser = 'CallUser',

  // Queries
  QuerySubmitPhoneNumber = 'QuerySubmitPhoneNumber',
  QuerySubmitTwoFactorStatus = 'QuerySubmitTwoFactorStatus',
  QuerySendDisableCode = 'QuerySendDisableCode',
  QueryGenerateOtp = 'QueryGenerateOtp',
  QueryCallSetupCode = 'QueryCallSetupCode',
}

interface DialogProps {
  title: string;
  message: string;
  closeModal?: boolean;
}

interface NewFieldError {
  fieldName: string;
  errorMessage: string;
}

interface FieldError {
  errors: Array<string>;
  name: string;
}

interface StepperStep {
  blockBack?: boolean;
  isFinal?: boolean;
  step: VerificationStep;
  title: string;
}

interface GenerateOtpResponse {
  secretKey: string;
  temporaryMfaSetupId: string;
}

interface GenerateSmsResponse {
  temporaryMfaSetupId: string;
}

interface State {
  isLoading: boolean;
  isEnabled: boolean;

  phoneNumber: string;
  smsCountry: SmsCountry;
  code: string;

  verificationMethod: TwoFactorAuthenticationType;
  verificationStep: number;

  steps: Array<StepperStep>;

  queries: Array<Action<ActionType>>;
  fieldErrors: Array<FieldError>;
  dialog: DialogProps | null;
  otpSecretKey: string | null;
  temporaryMfaSetupId: string | null;
  isUnableToScan: boolean;
  otpAccountName: string;

  twoFactorAuthenticationDisabler: TwoFactorAuthentication;

  /**
   * If an existing method was selected
   */
  twoFactorAuthentication?: TwoFactorAuthentication;
  /**
   * If any exist (during new 2FA addition)
   */
  twoFactorAuthentications?: Array<TwoFactorAuthentication>;
  isMaxNumberOfAuthMethodsReachedForCurrentType: boolean;
}

function reducer(state: State, action: Action<ActionType>): State {
  const { type, payload } = action;
  switch (type) {
    case ActionType.AddErrors:
      return {
        ...state,
        isLoading: false,
      };

    case ActionType.SetFormPhoneNumber: {
      const phoneNumber = (payload as string).replace(/[^0-9]+/g, ''); // Don't
      if (phoneNumber.length > 10) {
        // Max phone length
        return state;
      }

      // clear any field errors.
      const fieldErrors = state.fieldErrors.filter((e) => e.name !== 'phoneNumber');

      return {
        ...state,
        phoneNumber,
        fieldErrors,
      };
    }

    case ActionType.SetFormCountry:
      return {
        ...state,
        smsCountry: payload,
      };

    case ActionType.UnableToScan:
      return {
        ...state,
        isUnableToScan: true,
      };

    case ActionType.SetCode: {
      const code = (payload as string).replace(/[^0-9]+/g, '');
      if (code.length > 6) {
        // Max sms code length
        return state;
      }

      // clear any field errors.
      const fieldErrors = state.fieldErrors.filter((e) => e.name !== 'code');

      return {
        ...state,
        code,
        fieldErrors,
      };
    }

    case ActionType.SetFormVerificationMethod:
      return {
        ...state,
        verificationMethod: payload,
        isMaxNumberOfAuthMethodsReachedForCurrentType: isMaxNumberOfTwoFactorAuthReached(
          payload,
          state.twoFactorAuthentications
        ),
      };

    case ActionType.PreviousStep:
      return {
        ...state,
        verificationStep: state.verificationStep - 1,
      };

    case ActionType.NextStep: {
      const currentStep = state.steps[state.verificationStep];
      switch (currentStep.step) {
        case VerificationStep.SelectVerificationProvider: {
          if (state.verificationMethod !== TwoFactorAuthenticationType.Otp) {
            break;
          }

          return {
            ...state,
            isLoading: true,
            queries: [
              {
                type: ActionType.QueryGenerateOtp,
                payload: {},
              },
            ],
          };
        }

        case VerificationStep.EnterPhoneNumber: {
          return {
            ...state,
            isLoading: true,
            queries: [
              {
                type: ActionType.QuerySubmitPhoneNumber,
                payload: {
                  phoneCountry: state.smsCountry.toString(),
                  phoneNumber: state.phoneNumber,
                },
              },
            ],
          };
        }

        case VerificationStep.ConfirmOtpCode:
        case VerificationStep.EnterSmsCode: {
          /**
           * If we're disabling 2FA.
           */
          const { twoFactorAuthenticationDisabler, twoFactorAuthentication } = state;

          return {
            ...state,
            isLoading: true,
            queries: [
              {
                type: ActionType.QuerySubmitTwoFactorStatus,
                payload: {
                  code: state.code,
                  temporaryMfaSetupId: state.temporaryMfaSetupId,
                  type: state.verificationMethod,
                  closeOnSuccess: currentStep.isFinal,
                  /**
                   * If we're disabling 2FA
                   */
                  twoFactorAuthentication: twoFactorAuthenticationDisabler,
                  twoFactorAuthenticationIdToUpdate: twoFactorAuthentication?.id,
                },
              },
            ],
          };
        }

        case VerificationStep.ConfirmDisableOtpCode:
        case VerificationStep.EnterDisableSmsCode: {
          // @todo handle state change
          return {
            ...state,
          };
        }

        case VerificationStep.SelectDisabler:
          if (state.twoFactorAuthenticationDisabler.type === TwoFactorAuthenticationType.Otp) {
            return {
              ...state,
              verificationStep: state.verificationStep + 1,
            };
          }

          return {
            ...state,
            isLoading: true,
            queries: [
              {
                type: ActionType.QuerySendDisableCode,
                payload: {
                  twoFactorAuthenticationDisabler: state.twoFactorAuthenticationDisabler,
                },
              },
            ],
          };
      }

      return {
        ...state,
        verificationStep: state.verificationStep + 1,
      };
    }

    case ActionType.ResendSmsCode: {
      const query = state.isEnabled
        ? {
            type: ActionType.QuerySendDisableCode,
            payload: {
              twoFactorAuthenticationDisabler: state.twoFactorAuthenticationDisabler,
            },
          }
        : {
            type: ActionType.QuerySubmitPhoneNumber,
            payload: {
              phoneCountry: state.smsCountry.toString(),
              phoneNumber: state.phoneNumber,
            },
          };

      return {
        ...state,
        isLoading: true,
        queries: [query],
      };
    }

    case ActionType.CallUser: {
      const query = state.isEnabled
        ? {
            type: ActionType.QuerySendDisableCode,
            payload: {
              twoFactorAuthenticationDisabler: state.twoFactorAuthenticationDisabler,
              shouldCallUser: true,
            },
          }
        : {
            type: ActionType.QueryCallSetupCode,
            payload: {
              phoneCountry: state.smsCountry.toString(),
              phoneNumber: state.phoneNumber,
            },
          };

      return {
        ...state,
        queries: [query],
      };
    }

    case ActionType.SetSteps: {
      const newSteps = [];
      if (state.isEnabled && state.twoFactorAuthentications.length > 1) {
        newSteps.push({
          step: VerificationStep.SelectDisabler,
          title: 'Choose your Two-Step Provider to Disable Old Two-Step Provider',
        });
      }

      if (!state.isEnabled) {
        newSteps.push({
          step: VerificationStep.SelectVerificationProvider,
          title: `Choose Your ${state.isEnabled ? 'New ' : ''}Two-Step Provider`,
          blockBack: true,
        });

        const methodSpecificActivation =
          state.verificationMethod === TwoFactorAuthenticationType.Otp
            ? [
                { step: VerificationStep.OtpQrCode, title: 'Add to Authenticator (Scan QR Code)' },
                { step: VerificationStep.ConfirmOtpCode, title: 'Verify Authenticator' },
              ]
            : [
                { step: VerificationStep.EnterPhoneNumber, title: 'Enter Verification Details' },
                { step: VerificationStep.EnterSmsCode, title: 'Enter SMS Verification Code' },
              ];

        newSteps.push(...methodSpecificActivation);
      } else if (state.verificationMethod === TwoFactorAuthenticationType.Otp) {
        newSteps.push({ step: VerificationStep.ConfirmOtpCode, title: 'Enter Authenticator Code' });
      } else {
        newSteps.push({
          step: VerificationStep.EnterSmsCode,
          title: 'Enter SMS Verification Code',
        });
      }

      newSteps[newSteps.length - 1].isFinal = true;
      return {
        ...state,
        steps: newSteps,
      };
    }

    case ActionType.ShowDialog: {
      const dialog = payload as DialogProps;

      return {
        ...state,
        dialog,
        isLoading: false,
      };
    }

    case ActionType.CloseDialog:
      return {
        ...state,
        dialog: null,
      };

    case ActionType.ShowFieldError: {
      const { fieldName, errorMessage } = payload as NewFieldError;

      return {
        ...state,
        code: '', // Clear code
        isLoading: false,
        fieldErrors: [{ name: fieldName, errors: [errorMessage] }],
      };
    }

    case ActionType.HandleFormFieldErrors: {
      const { fields: fieldErrors } = payload;

      return {
        ...state,
        isLoading: false,
        fieldErrors,
      };
    }

    case ActionType.SetDisableVerificationMethod:
      return {
        ...state,
        twoFactorAuthenticationDisabler: payload,
        verificationMethod: payload.type,
      };

    // Handle Query responses
    case ActionType.QuerySubmitTwoFactorStatus:
    case ActionType.QuerySubmitPhoneNumber:
    case ActionType.QueryCallSetupCode:
    case ActionType.QuerySendDisableCode: {
      const queries = [];
      const nextStep = state.steps[state.verificationStep + 1];
      if (nextStep?.step === VerificationStep.EnterDisableSmsCode) {
        queries.push({ type: ActionType.QuerySendDisableCode });
      }

      const { temporaryMfaSetupId } = payload as GenerateSmsResponse;
      return {
        ...state,
        verificationStep: state.verificationStep + (nextStep ? 1 : 0),
        code: '',
        queries,
        isLoading: false,
        fieldErrors: [],
        temporaryMfaSetupId,
      };
    }

    case ActionType.QueryGenerateOtp: {
      const { secretKey, temporaryMfaSetupId } = payload as GenerateOtpResponse;

      return {
        ...state,
        verificationStep: state.verificationStep + 1,
        isLoading: false,
        otpSecretKey: secretKey,
        temporaryMfaSetupId,
      };
    }
  }

  return state;
}

async function handleQueries(
  queries: Array<Action<ActionType>>,
  dispatch: Dispatch<Action<ActionType>>,
  onClose: { (close: boolean): void }
): Promise<void> {
  const handleError = (error: ApiError) => {
    if (error && error.type) {
      const getErrorDialog = (): DialogProps => {
        switch (error.type) {
          case 'invalid.code':
            dispatch({
              type: ActionType.ShowFieldError,
              payload: {
                errorMessage: error.message,
                fieldName: 'code',
              } as NewFieldError,
            });
            return undefined;

          case 'invalid.status.enabled':
            return {
              title: 'Already enabled',
              message: 'This two-step authentication is already enabled.',
              closeModal: true,
            };

          case 'invalid.status.disabled':
            return {
              title: 'Is not enabled',
              message: 'You can only disable two-step authentications that are already enabled.',
              closeModal: true,
            };

          case 'invalid.switch.status.disabled':
            return {
              title: 'Is not enabled',
              message: 'You can only switch two-step authentications that are already enabled.',
              closeModal: true,
            };

          case 'invalid.switch.number.duplicate':
            return {
              title: 'Duplicate phone number',
              message: 'You can only switch the authentication to a new number.',
              closeModal: true,
            };

          case 'invalid.twoFactorForced':
            return {
              title: 'Two Factor is mandatory',
              message:
                'You can not disable your two-step authentication because your company administrator has requested a mandatory two-step authentication.',
              closeModal: true,
            };

          case 'invalid.twoFactorCompliance':
            return {
              title: 'Two Factor is mandatory',
              message:
                "You can not disable your two factor authentication because your account is required to have it enabled as per Plooto's security requirements.",
              closeModal: true,
            };

          case 'duplicate.phone.generate.code':
            dispatch({
              type: ActionType.ShowFieldError,
              payload: {
                errorMessage: error.message,
                fieldName: 'phoneNumber',
              } as NewFieldError,
            });
            return undefined;

          case 'duplicate.phone.add.mfa':
            return {
              title: 'Duplicate phone number',
              message:
                'This phone number is already registered for multi-factor authentication via SMS.',
              closeModal: true,
            };

          case 'invalid.phoneCountry':
            return {
              title: 'Unsupported country',
              message: error.message,
              closeModal: true,
            };

          case 'rateLimit.smsSetup':
            return {
              title: 'Rate limit error',
              message:
                'Only 5 codes for new device registration allowed per hour. Please try again later.',
              closeModal: true,
            };

          case 'invalid.temporaryMfaSetupId':
            return {
              title: 'Invalid setup ID',
              message: 'Invalid MFA setup ID or it has expired. Please try again later.',
              closeModal: true,
            };

          case 'invalid.temporaryMfaSetupId.userId':
            return {
              title: 'Invalid user ID',
              message: 'Invalid user ID. Please try again later.',
              closeModal: true,
            };

          case 'invalid.temporaryMfaSetupId.isDeleted':
            return {
              title: 'An issue with this MFA setup',
              message: 'There was an issue with this MFA setup. Please try again later.',
              closeModal: true,
            };

          case 'invalid.temporaryMfaSetupId.deviceId':
            return {
              title: 'Invalid Cookie Settings',
              message:
                'Your browser privacy settings must allow cookies to be set to enable Two Factor Authentication.',
              closeModal: true,
            };

          default: {
            return {
              title: 'Unexpected Error',
              message: `There was an error submitting your code. Please try again later. Error code: ${error.type}`,
            };
          }
        }
      };

      switch (error.type) {
        case 'invalid.parameter':
          dispatch({ type: ActionType.HandleFormFieldErrors, payload: error });
          return;

        default: {
          const errorDialog = getErrorDialog();
          if (errorDialog) {
            dispatch({ type: ActionType.ShowDialog, payload: errorDialog });
          }
        }
      }
    } else {
      const errorDialog: DialogProps = {
        title: 'Unexpected Error',
        message: 'There was an error submitting your code. Please try again later.',
      };
      // @todo log error to App Insights
      dispatch({ type: ActionType.ShowDialog, payload: errorDialog });
    }
  };

  queries.forEach(async (query) => {
    const { type, payload } = query;

    switch (type) {
      case ActionType.QuerySubmitPhoneNumber: {
        const { phoneCountry, phoneNumber } = payload;

        if (!phoneNumber) {
          dispatch({
            type: ActionType.ShowFieldError,
            payload: {
              errorMessage: 'Phone Number is required',
              fieldName: 'phoneNumber',
            } as NewFieldError,
          });
          return;
        }

        try {
          const smsCodeResponse = await TwoFactorAuthenticationService.generateSmsCode({
            phoneCountry,
            phoneNumber,
          });
          dispatch({ type, payload: smsCodeResponse });
        } catch (e) {
          handleError(e);
        }
        break;
      }

      case ActionType.QueryCallSetupCode: {
        const { phoneCountry, phoneNumber } = payload;

        try {
          const smsCodeResponse = await TwoFactorAuthenticationService.generateSmsCode({
            phoneCountry,
            phoneNumber,
            shouldCallUser: true,
          });
          dispatch({ type, payload: smsCodeResponse });
        } catch (e) {
          handleError(e);
        }
        break;
      }

      case ActionType.QuerySubmitTwoFactorStatus: {
        const {
          code,
          temporaryMfaSetupId,
          type: mfaType,
          closeOnSuccess,
          twoFactorAuthentication,
          twoFactorAuthenticationIdToUpdate,
        } = payload;

        if (!code) {
          let errorMessage: string;
          switch (mfaType) {
            case TwoFactorAuthenticationType.Otp:
              errorMessage = 'Authenticator Code is required';
              break;
            case TwoFactorAuthenticationType.Sms:
              errorMessage = 'SMS Code is required';
              break;
          }

          dispatch({
            type: ActionType.ShowFieldError,
            payload: {
              errorMessage,
              fieldName: 'code',
            } as NewFieldError,
          });
          return;
        }

        try {
          if (!twoFactorAuthentication) {
            await TwoFactorAuthenticationService.addTwoFactorAuthentication({
              code,
              temporaryMfaSetupId,
              type: mfaType,
            });
          } else {
            await TwoFactorAuthenticationService.updateTwoFactorAuthentication(
              twoFactorAuthentication.id,
              {
                code,
                status: TwoFactorAuthenticationStatus.Disabled,
                twoFactorAuthenticationIdToUpdate,
              }
            );
          }

          if (closeOnSuccess) {
            onClose(true);
          } else {
            dispatch({ type });
          }
        } catch (e) {
          handleError(e);
        }

        break;
      }

      case ActionType.QuerySendDisableCode: {
        const { id } = payload.twoFactorAuthenticationDisabler as TwoFactorAuthentication;
        const { shouldCallUser } = payload;

        try {
          const smsSettings = await TwoFactorAuthenticationService.sendDisableCode(id, {
            shouldCallUser,
          });
          dispatch({ type, payload: smsSettings });
        } catch (e) {
          handleError(e);
        }
        break;
      }

      case ActionType.QueryGenerateOtp: {
        try {
          const otpCodeResponse = await TwoFactorAuthenticationService.generateOtpCode();
          dispatch({ type, payload: otpCodeResponse });
        } catch (e) {
          handleError(e);
        }
        break;
      }
    }
  });
}

interface StateInitialValues {
  twoFactorAuthentication: TwoFactorAuthentication | null;
  twoFactorAuthentications: Array<TwoFactorAuthentication> | null;
}

const isMaxNumberOfTwoFactorAuthReached = (
  twoFactorAuthenticationType: TwoFactorAuthenticationType,
  twoFactorAuthentications: Array<TwoFactorAuthentication>
) =>
  twoFactorAuthentications?.filter((tfa) => tfa.type === twoFactorAuthenticationType)?.length ===
  Constants.MAX_NUMBER_OF_2FAS_PER_TYPE;

function getInitialState(initialValues: StateInitialValues): State {
  const { twoFactorAuthentication, twoFactorAuthentications } = initialValues;

  const queries = [];

  const isMaxNumberOfOtpTwoFactorAuthReached = isMaxNumberOfTwoFactorAuthReached(
    TwoFactorAuthenticationType.Otp,
    twoFactorAuthentications
  );

  const isMaxNumberOfSmsTwoFactorAuthReached = isMaxNumberOfTwoFactorAuthReached(
    TwoFactorAuthenticationType.Sms,
    twoFactorAuthentications
  );

  let verificationMethod: TwoFactorAuthenticationType;
  if (isMaxNumberOfOtpTwoFactorAuthReached) {
    verificationMethod = TwoFactorAuthenticationType.Sms;
  } else if (isMaxNumberOfSmsTwoFactorAuthReached) {
    verificationMethod = TwoFactorAuthenticationType.Otp;
  } else {
    verificationMethod = TwoFactorAuthenticationType.Otp;
  }

  let isMaxNumberOfAuthMethodsReachedForCurrentType;
  switch (verificationMethod) {
    case TwoFactorAuthenticationType.Otp:
      isMaxNumberOfAuthMethodsReachedForCurrentType = isMaxNumberOfOtpTwoFactorAuthReached;
      break;
    case TwoFactorAuthenticationType.Sms:
      isMaxNumberOfAuthMethodsReachedForCurrentType = isMaxNumberOfSmsTwoFactorAuthReached;
      break;
  }

  let twoFactorAuthenticationDisabler: TwoFactorAuthentication;
  if (twoFactorAuthentication) {
    // Only 1 possible disable method.
    if (!twoFactorAuthentications.length || twoFactorAuthentications.length === 1) {
      twoFactorAuthenticationDisabler = twoFactorAuthentication;

      verificationMethod = twoFactorAuthenticationDisabler.type;

      if (twoFactorAuthenticationDisabler.type === TwoFactorAuthenticationType.Sms) {
        queries.push({
          type: ActionType.QuerySendDisableCode,
          payload: {
            twoFactorAuthenticationDisabler,
          },
        });
      }
    } else {
      [twoFactorAuthenticationDisabler] = twoFactorAuthentications;
    }
  }

  return {
    isLoading: false,
    isEnabled: !!twoFactorAuthentication,

    phoneNumber: '',
    smsCountry:
      companyService.Instance.CompanyCountry() === SmsCountry.CA ? SmsCountry.CA : SmsCountry.US,

    // Ensure we always have something filled in case 2FA was required and email couldn't be loaded.
    // We'll try their email, then their name, then their User ID, and then in the weird case where
    // we have nothing, the URL of the Plooto environment they signed up from (which should surely
    // never be used, but it will always be defined).
    otpAccountName:
      UserService.DisplayEmail() ||
      UserService.DisplayName() ||
      UserService.UserId() ||
      import.meta.env.APP_URLS_APP_URL,

    code: '',

    otpSecretKey: null,
    temporaryMfaSetupId: null,

    steps: [],

    verificationMethod,
    verificationStep: 0,

    queries,
    fieldErrors: [],
    dialog: null,
    isUnableToScan: false,
    twoFactorAuthentication, // Currently selected 2FA method
    twoFactorAuthentications, // Previously created 2FAs,
    isMaxNumberOfAuthMethodsReachedForCurrentType,
    twoFactorAuthenticationDisabler,
  };
}

export { State, ActionType, getInitialState, handleQueries, reducer };
